import styled from 'styled-components';
import { Link } from 'react-router-dom';
import HeroImageFile from './hero-apps.png';

// ========================================
// STYLED COMPONENTS
// ========================================

const PageContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 80px 100px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  color: white;
  min-height: 500px;

  @media (max-width: 968px) {
    flex-direction: column;
    padding: 60px 40px;
    text-align: center;
  }

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  max-width: 600px;

  @media (max-width: 968px) {
    max-width: 100%;
  }
`;

const Headline = styled.h1`
  font-size: 48px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 20px;
  color: white;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Subheadline = styled.p`
  font-size: 20px;
  line-height: 1.6;
  margin-bottom: 30px;
  color: rgba(255, 255, 255, 0.9);

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const HeroStats = styled.div`
  display: flex;
  gap: 40px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Stat = styled.div`
  text-align: left;

  @media (max-width: 968px) {
    text-align: center;
  }
`;

const StatIcon = styled.div`
  font-size: 28px;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const PrimaryButton = styled.a`
  padding: 15px 30px;
  background-color: #8ecf41;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-block;

  &:hover {
    background-color: #72b237;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(142, 207, 65, 0.3);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background-color: transparent;
  border: 2px solid white;
  color: white;

  &:hover {
    background-color: white;
    color: #453793;
  }
`;

const HeroImage = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
  }

  @media (max-width: 968px) {
    margin-top: 40px;
    max-width: 400px;
  }
`;

// ========================================
// SHARED
// ========================================

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: #333;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  text-align: center;
  color: #666;
  max-width: 700px;
  margin: 0 auto 60px;
  line-height: 1.6;
`;

// ========================================
// OUR APPS SECTION
// ========================================

const AppsSection = styled.section`
  padding: 80px 100px;
  background: white;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const AppCard = styled.div`
  padding: 40px 30px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    transform: translateY(-5px);
    border-color: #8ecf41;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  .app-logo img {
    height: 80px;
    width: auto;
    margin-bottom: 15px;
  }

  .app-emoji {
    font-size: 60px;
    margin-bottom: 15px;
  }

  h3 {
    font-size: 26px;
    margin-bottom: 10px;
    color: #453793;
    font-weight: 700;
  }

  p {
    font-size: 15px;
    line-height: 1.6;
    color: #666;
    margin-bottom: 24px;
  }
`;

const AppStatus = styled.div`
  display: inline-block;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 15px;
  background: ${props => props.live ? 'rgba(142, 207, 65, 0.15)' : 'rgba(69, 55, 147, 0.1)'};
  color: ${props => props.live ? '#5a9e2f' : '#453793'};
`;

const PlayStoreBadge = styled.a`
  display: inline-block;
  margin-bottom: 12px;
  img {
    height: 44px;
    width: auto;
  }
`;

const AppNote = styled.div`
  font-size: 13px;
  color: #888;

  a {
    color: #453793;
    text-decoration: none;
    font-weight: 500;
    &:hover { text-decoration: underline; }
  }
`;

// ========================================
// TECH STRIP
// ========================================

const TechStrip = styled.div`
  background: #f8f9fa;
  padding: 30px 100px;
  text-align: center;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;

  p {
    font-size: 12px;
    color: #aaa;
    margin-bottom: 14px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
  }

  .badges {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: center;
  }

  .badge {
    padding: 6px 16px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    font-size: 13px;
    color: #555;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

// ========================================
// BACKGROUND SECTION
// ========================================

const BackgroundSection = styled.section`
  padding: 80px 100px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: 600;
`;

const ProjectContent = styled.div`
  padding: 30px;

  h3 {
    font-size: 22px;
    margin-bottom: 10px;
    color: #333;
  }

  .client {
    font-size: 14px;
    color: #8ecf41;
    font-weight: 600;
    margin-bottom: 15px;
  }

  p {
    font-size: 15px;
    line-height: 1.6;
    color: #666;
    margin-bottom: 20px;
  }

  .metrics {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .metric {
    font-size: 13px;
    color: #666;
    padding: 6px 12px;
    background: #f5f5f5;
    border-radius: 4px;
  }
`;

const ProjectLink = styled(Link)`
  color: #453793;
  font-weight: 600;
  text-decoration: none;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;

  &:hover {
    color: #8ecf41;
    gap: 10px;
  }

  &:after {
    content: "→";
  }
`;

// ========================================
// WHY ZUMOSOFT SECTION
// ========================================

const WhySection = styled.section`
  padding: 80px 100px;
  background: white;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Feature = styled.div`
  text-align: center;

  .icon {
    font-size: 48px;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 20px;
    margin-bottom: 10px;
    color: #333;
  }

  p {
    font-size: 15px;
    line-height: 1.6;
    color: #666;
  }
`;

// ========================================
// CTA SECTION
// ========================================

const CTASection = styled.section`
  padding: 80px 100px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  text-align: center;
  color: white;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }

  h2 {
    font-size: 36px;
    margin-bottom: 20px;
  }

  p {
    font-size: 18px;
    margin-bottom: 40px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    color: rgba(255, 255, 255, 0.9);
  }
`;

// ========================================
// COMPONENT
// ========================================

function HomePage() {
    return (
        <PageContainer>
            {/* HERO SECTION */}
            <HeroSection>
                <HeroContent>
                    <Headline>We Build the Apps<br />We Wish Existed.</Headline>
                    <Subheadline>
                        Zumosoft is an indie mobile app studio. We solve real problems,
                        starting with our own, built on React Native and Azure.
                    </Subheadline>
                    <HeroStats>
                        <Stat>
                            <StatIcon>📱</StatIcon>
                            <StatLabel>Live on Android</StatLabel>
                        </Stat>
                        <Stat>
                            <StatIcon>🍎</StatIcon>
                            <StatLabel>iOS late 2026</StatLabel>
                        </Stat>
                        <Stat>
                            <StatIcon>☁️</StatIcon>
                            <StatLabel>Built on Azure</StatLabel>
                        </Stat>
                    </HeroStats>
                    <CTAButtons>
                        <PrimaryButton
                            href="https://play.google.com/store/apps/details?id=com.zumodo"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Try ZumoDO Free for 14 Days
                        </PrimaryButton>
                        <SecondaryButton as={Link} to="/zumodo">
                            iOS Waitlist →
                        </SecondaryButton>
                    </CTAButtons>
                </HeroContent>
                <HeroImage>
                    <img src={HeroImageFile} alt="Zumosoft Apps" />
                </HeroImage>
            </HeroSection>

            {/* OUR APPS SECTION */}
            <AppsSection>
                <SectionTitle>Our Apps</SectionTitle>
                <SectionSubtitle>
                    Five apps. Five real problems. Built for real people. Not enterprise dashboards,
                    not demos. Apps we actually use every day.
                </SectionSubtitle>
                <AppsGrid>
                    <AppCard>
                        <div className="app-logo">
                            <img src="/zumodo/images/ZumoDO_logo_sm.png" alt="ZumoDO" />
                        </div>
                        <AppStatus live>● Live on Android</AppStatus>
                        <h3>ZumoDO</h3>
                        <p>
                            Tasks, goals, habits, and calendar. Organized by Morning, Afternoon,
                            Evening, and Night so your to-do list matches how you actually live.
                        </p>
                        <PlayStoreBadge
                            href="https://play.google.com/store/apps/details?id=com.zumodo"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                alt="Get it on Google Play"
                            />
                        </PlayStoreBadge>
                        <AppNote>
                            🍎 iOS launching late 2026. <Link to="/zumodo">Join the waitlist.</Link>
                        </AppNote>
                    </AppCard>

                    <AppCard>
                        <div className="app-emoji">🏠</div>
                        <AppStatus>◎ In Development</AppStatus>
                        <h3>ZumoPATH</h3>
                        <p>
                            The home buying navigator nobody built yet. Real mortgage credit scores,
                            debt payoff tools, plain-English program guides, and honest advice.
                            No sales pitch.
                        </p>
                        <AppNote>
                            Coming 2027 · <Link to="/contact">Get notified</Link>
                        </AppNote>
                    </AppCard>

                    <AppCard>
                        <div className="app-emoji">🥦</div>
                        <AppStatus>◎ In Development</AppStatus>
                        <h3>EAT 54321</h3>
                        <p>
                            Healthy eating made simple. 5 vegetables, 4 whole grains, 3 proteins,
                            2 fruits, 1 nuts or seeds. Every day. No calorie math. Just check the boxes
                            and feel good about it.
                        </p>
                        <AppNote>
                            <Link to="/contact">Get notified</Link>
                        </AppNote>
                    </AppCard>

                    <AppCard>
                        <div className="app-emoji">🤝</div>
                        <AppStatus>◎ Coming Soon</AppStatus>
                        <h3>ZumoGIVE</h3>
                        <p>
                            Connecting charities to the people and skills they actually need.
                            "This shelter needs a new roof. Does anyone have roofing experience?"
                            Community matchmaking for good.
                        </p>
                        <AppNote>
                            Coming 2027 · <Link to="/contact">Get notified</Link>
                        </AppNote>
                    </AppCard>

                    <AppCard>
                        <div className="app-emoji">📚</div>
                        <AppStatus>◎ Coming Soon</AppStatus>
                        <h3>One More Thing...</h3>
                        <p>
                            We're keeping this one close to the chest. It's nothing like our other apps.
                            All we'll say is — you're going to want to lose yourself in it.
                        </p>
                        <AppNote>
                            Coming 2027 · <Link to="/contact">Get notified</Link>
                        </AppNote>
                    </AppCard>
                </AppsGrid>
            </AppsSection>

            {/* TECH STRIP */}
            <TechStrip>
                <p>Built with</p>
                <div className="badges">
                    <span className="badge">React Native</span>
                    <span className="badge">Azure Cosmos DB</span>
                    <span className="badge">Azure OpenAI</span>
                    <span className="badge">Azure Web App</span>
                    <span className="badge">Node.js</span>
                    <span className="badge">Firebase Auth</span>
                </div>
            </TechStrip>

            {/* BACKGROUND SECTION */}
            <BackgroundSection>
                <SectionTitle>Our Background</SectionTitle>
                <SectionSubtitle>
                    Two decades of enterprise software experience backing every app we build.
                </SectionSubtitle>
                <ProjectsGrid>
                    <ProjectCard>
                        <ProjectImage>
                            <img src="/zumodo/images/ZumoDO_logo_sm.png" alt="ZumoDO" style={{ maxWidth: '25%', height: 'auto', marginRight: '15px' }} />
                            ZumoDO
                        </ProjectImage>
                        <ProjectContent>
                            <div className="client">Zumosoft Product</div>
                            <h3>Productivity & Wellness App</h3>
                            <p>
                                All-in-one mobile app combining task management, goal tracking,
                                lifestyle habits, and AI assistance.
                            </p>
                            <div className="metrics">
                                <div className="metric">React Native</div>
                                <div className="metric">Azure + Cosmos DB</div>
                                <div className="metric">Live on Android</div>
                            </div>
                            <ProjectLink to="/zumodo">Learn More</ProjectLink>
                        </ProjectContent>
                    </ProjectCard>

                    <ProjectCard>
                        <ProjectImage>MedExplain</ProjectImage>
                        <ProjectContent>
                            <div className="client">MedExplain Health</div>
                            <h3>Multilingual Health Education Platform</h3>
                            <p>
                                Solo architect for complete platform with AI-powered English→Spanish translation.
                            </p>
                            <div className="metrics">
                                <div className="metric">2,000+ daily users</div>
                                <div className="metric">React + Cosmos DB</div>
                                <div className="metric">Azure AI Services</div>
                                <div className="metric">HIPAA-ready</div>
                            </div>
                            <ProjectLink to="/clients">View Case Study</ProjectLink>
                        </ProjectContent>
                    </ProjectCard>

                    <ProjectCard>
                        <ProjectImage>Gateway</ProjectImage>
                        <ProjectContent>
                            <div className="client">Ilitch Holdings</div>
                            <h3>Enterprise Application Platform</h3>
                            <p>
                                Built platform serving 4,000+ franchisees with customizable dashboards
                                and real-time alerts.
                            </p>
                            <div className="metrics">
                                <div className="metric">4,000+ users</div>
                                <div className="metric">React + Flutter</div>
                                <div className="metric">ServiceNow integration</div>
                            </div>
                            <ProjectLink to="/clients">View Case Study</ProjectLink>
                        </ProjectContent>
                    </ProjectCard>

                    <ProjectCard>
                        <ProjectImage>M365 Migration</ProjectImage>
                        <ProjectContent>
                            <div className="client">Ilitch Holdings</div>
                            <h3>Enterprise M365 Transformation</h3>
                            <p>
                                Led 5,000-user migration across 8 companies. Received IT Impact Award.
                            </p>
                            <div className="metrics">
                                <div className="metric">5,000+ users</div>
                                <div className="metric">40% adoption increase</div>
                                <div className="metric">30% cost reduction</div>
                            </div>
                            <ProjectLink to="/clients">View Case Study</ProjectLink>
                        </ProjectContent>
                    </ProjectCard>
                </ProjectsGrid>
            </BackgroundSection>

            {/* WHY ZUMOSOFT */}
            <WhySection>
                <SectionTitle>Why Zumosoft</SectionTitle>
                <SectionSubtitle>
                    Apps built by someone who has shipped real software at real scale,
                    and who uses every app she builds.
                </SectionSubtitle>
                <FeaturesGrid>
                    <Feature>
                        <div className="icon">🛠️</div>
                        <h3>Built to Use, Not to Demo</h3>
                        <p>Every app solves a problem the founder actually has. ZumoDO runs her day. ZumoPATH navigates her finances. Real use, real feedback, real improvements.</p>
                    </Feature>

                    <Feature>
                        <div className="icon">☁️</div>
                        <h3>Azure-Native Stack</h3>
                        <p>Cosmos DB, Azure OpenAI, Azure Web App. Enterprise-grade infrastructure at consumer price points, built to scale without rearchitecting.</p>
                    </Feature>

                    <Feature>
                        <div className="icon">🏆</div>
                        <h3>Award-Winning Track Record</h3>
                        <p>Ilitch Holdings IT Impact Award. Led 5,000-user enterprise migrations. Solo-architected platforms serving thousands of daily users.</p>
                    </Feature>

                    <Feature>
                        <div className="icon">🤝</div>
                        <h3>Diverse Teams Win</h3>
                        <p>Built the most diverse IT team at Ilitch Holdings, and also the most productive. Different perspectives build stronger products.</p>
                    </Feature>

                    <Feature>
                        <div className="icon">👩‍💼</div>
                        <h3>Women-Owned Startup</h3>
                        <p>WOSB certification in progress. Building consumer apps that serve everyone, especially those underserved by mainstream tech.</p>
                    </Feature>

                    <Feature>
                        <div className="icon">🎯</div>
                        <h3>No Complexity for Complexity's Sake</h3>
                        <p>Clean, focused apps. Nothing you don't need. Everything you do. That's the Zumosoft standard.</p>
                    </Feature>
                </FeaturesGrid>
            </WhySection>

            {/* FINAL CTA */}
            <CTASection>
                <h2>Ready to Try ZumoDO?</h2>
                <p>
                    14-day free trial on Android. iOS launching late 2026.<br />
                    Get organized the way you actually live.
                </p>
                <CTAButtons style={{ justifyContent: 'center' }}>
                    <PrimaryButton
                        href="https://play.google.com/store/apps/details?id=com.zumodo"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Try Free for 14 Days
                    </PrimaryButton>
                    <SecondaryButton as={Link} to="/zumodo">
                        🍎 Join iOS Waitlist
                    </SecondaryButton>
                </CTAButtons>
            </CTASection>
        </PageContainer>
    );
}

export default HomePage;
